import type { AutomationRunRefDto } from '@tms/shared';
import { useErrorText, useFormat, useT } from '../../../i18n';
import { AutomationError } from '../client';
import { useTriggerLabel } from '../ui';
import type { GroupVerdict, Verdict } from './preflight';

/** «12 s», «3 min 05 s», «1 h 02 min». */
export const useDuration = () => {
  const { t } = useT();
  return (ms: number): string => {
    const s = Math.round(ms / 1000);
    if (s < 60) return t('automation.runs.duration.seconds', { s });
    const m = Math.floor(s / 60);
    if (m < 60) {
      return t('automation.runs.duration.minutes', { m, s: String(s % 60).padStart(2, '0') });
    }
    return t('automation.runs.duration.hours', {
      h: Math.floor(m / 60),
      m: String(m % 60).padStart(2, '0'),
    });
  };
};

/** An error of a TMS or TestRun call in words; TestRun out of reach says so. */
export const useRunsErrorText = () => {
  const { t } = useT();
  const { errorText } = useErrorText();
  return (error: unknown): string =>
    error instanceof AutomationError && error.status === 0
      ? t('automation.unreachable')
      : errorText(error);
};

/** An earlier run in a picker: «#123 · on push · 2 hours ago». */
export const useRunRefLabel = () => {
  const { t } = useT();
  const { relative } = useFormat();
  const triggerLabel = useTriggerLabel();
  return (ref: AutomationRunRefDto): string => {
    const who = triggerLabel(ref.trigger) ?? ref.user ?? t('automation.runs.byHere');
    return `${ref.runId} · ${who} · ${relative(ref.createdAt)}`;
  };
};

/** A pool account's fit for a class, and for a group of classes, in words. */
export const useVerdictText = () => {
  const { t } = useT();
  const text = (v: Verdict): string => {
    switch (v.reason) {
      case 'ok':
        return t('automation.runs.verdict.ok');
      case 'type':
        return v.detail ?? t('automation.runs.verdict.type');
      case 'spent':
        return t('automation.runs.verdict.spent', { detail: v.detail ?? '' });
      case 'missing':
        return t('automation.runs.verdict.missing', { list: v.detail ?? '' });
      case 'broken':
        return t('automation.runs.verdict.broken', { list: v.detail ?? '' });
      case 'notChecked':
        return t('automation.runs.verdict.notChecked', { list: v.detail ?? '' });
      default:
        return t('automation.runs.verdict.unchecked');
    }
  };
  const groupText = (g: GroupVerdict): string => {
    if (g.kind === 'ok') return t('automation.runs.verdict.ok');
    if (g.kind === 'missing') return [...new Set(g.verdicts.map(text))].join('; ');
    const first = g.verdicts[0];
    return first ? text(first) : t('automation.runs.verdict.unchecked');
  };
  /** with «fits 8 of 10 cl.» when a group's classes differ */
  const why = (g: GroupVerdict): string =>
    g.kind === 'missing' && g.of > 1
      ? t('automation.runs.verdict.fitOf', { fit: g.fit, of: g.of, text: groupText(g) })
      : groupText(g);
  return { text, groupText, why };
};
